/**
 * SmartField — reusable editable field renderers for task / entity tables.
 *
 * These are the same field components used by the in-chat confirm preview
 * (`TaskPreviewTable`), and are designed to be embedded anywhere else the
 * user reconciles data against fixed options: in task-management grids,
 * inline on task cards, in detail sheets, etc.
 *
 * Each field accepts `value` + `onChange(next)` and renders a polished,
 * themed control using the app's existing shadcn primitives and the global
 * `WorkAreaContext` / org members list.
 *
 *   <SmartTitleField />         free-text task title
 *   <SmartNotesField />         free-text description
 *   <SmartWorkAreaField />      <Select> populated from WorkAreaContext (id → name)
 *   <SmartDateField />          <Popover> + <Calendar> — same pattern we use everywhere
 *   <SmartPriorityField />      P0..P3 with colour coding
 *   <SmartEffortField />        E1..E5 friendly labels
 *   <SmartAssigneeField />      org members dropdown (fetched once, cached)
 *
 * The pure controls are intentionally small so they compose into BOTH
 * table rows AND detail forms.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { CalendarIcon, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '../lib/utils';
import { useWorkAreas } from '../contexts/WorkAreaContext';
import { PRIORITY_META, PRIORITY_OPTIONS, EFFORT_OPTIONS, formatEffortLabel } from '../lib/taskConstants';

// Re-exports for backward compatibility — most callers should import directly
// from '../lib/taskConstants'.
export const PRIORITY_OPTS = PRIORITY_OPTIONS;
export const EFFORT_OPTS = EFFORT_OPTIONS;

export const fmtDate = (s) => {
  if (!s) return '';
  try { return format(parseISO(s), 'EEE, MMM d'); } catch { return s; }
};


export function SmartTitleField({ value, onChange, placeholder = 'Title', testId, className }) {
  return (
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn('h-8 text-xs', className)}
      data-testid={testId}
    />
  );
}

export function SmartNotesField({ value, onChange, placeholder = 'Notes', testId, rows = 1, className }) {
  return (
    <Textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn('min-h-0 h-8 text-xs resize-y', className)}
      rows={rows}
      data-testid={testId}
    />
  );
}

export function SmartWorkAreaField({ value, onChange, testId, className, showNone = true }) {
  const { workAreas } = useWorkAreas();
  return (
    <Select
      value={value || '__none__'}
      onValueChange={(v) => onChange(v === '__none__' ? '' : v)}
    >
      <SelectTrigger className={cn('h-8 text-xs', className)} data-testid={testId}>
        <SelectValue placeholder="Pick a category" />
      </SelectTrigger>
      <SelectContent>
        {showNone && (
          <SelectItem value="__none__">
            <span className="text-muted-foreground">(none)</span>
          </SelectItem>
        )}
        {workAreas.map(w => (
          <SelectItem key={w.work_area_id} value={w.work_area_id}>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: w.color || '#94a3b8' }} />
              {w.name}
            </span>
          </SelectItem>
        ))}
        {workAreas.length === 0 && (
          <SelectItem value="__no__" disabled>No categories defined</SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

export function SmartDateField({ value, onChange, testId, className, placeholder = 'No date' }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 w-full justify-start text-left text-xs font-normal gap-2',
            !value && 'text-muted-foreground',
            className,
          )}
          data-testid={testId}
        >
          <CalendarIcon className="w-3 h-3" />
          {value ? fmtDate(value) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={value ? parseISO(value) : undefined}
          onSelect={(d) => onChange(d ? format(d, 'yyyy-MM-dd') : '')}
          initialFocus
        />
        {value && (
          <div className="p-2 border-t">
            <Button
              variant="ghost" size="sm" className="w-full h-7 text-xs"
              onClick={() => onChange('')}
            >
              Clear date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function SmartPriorityField({ value, onChange, testId, className }) {
  const key = String(value ?? 3);
  const meta = PRIORITY_META[key] || PRIORITY_META["3"];
  return (
    <Select value={key} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger
        className={cn('h-8 text-xs font-semibold border-0', className)}
        style={{ backgroundColor: meta.bg, color: meta.text }}
        data-testid={testId}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PRIORITY_OPTIONS.map(p => {
          const m = PRIORITY_META[p.key];
          return (
            <SelectItem key={p.key} value={p.key}>
              <span
                className="inline-block px-2 py-0.5 rounded font-bold text-[10px]"
                style={{ backgroundColor: m.bg, color: m.text }}
              >
                {p.label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

export function SmartEffortField({ value, onChange, testId, className }) {
  return (
    <Select value={String(value ?? 1)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className={cn('h-8 text-xs', className)} data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {EFFORT_OPTIONS.map(e => (
          <SelectItem key={e.value} value={String(e.value)}>
            {formatEffortLabel(e)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Module-level cache so every SmartAssigneeField across the app shares one fetch.
let _assigneesCache = null;
let _assigneesCacheAt = 0;
const ASSIGNEE_TTL_MS = 60_000;

async function _fetchAssignees() {
  const now = Date.now();
  if (_assigneesCache && (now - _assigneesCacheAt) < ASSIGNEE_TTL_MS) return _assigneesCache;
  try {
    const r = await fetch('/api/ai/v2/execute', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ capability_id: 'orka.org.list_members', input_data: {} }),
    });
    const d = await r.json();
    const list = (d?.output?.members || []).map(m => ({
      id: m.person_id || m.user_id || m.canonical_id || m.email,
      name: m.name || m.display_name || m.email,
      email: m.email || '',
    })).filter(m => m.id);
    _assigneesCache = list; _assigneesCacheAt = now;
    return list;
  } catch {
    return [];
  }
}

export function SmartAssigneeField({ value, onChange, testId, className, showNone = true }) {
  const [people, setPeople] = useState(_assigneesCache || []);
  const aborted = useRef(false);
  useEffect(() => {
    aborted.current = false;
    _fetchAssignees().then(list => { if (!aborted.current) setPeople(list); });
    return () => { aborted.current = true; };
  }, []);
  return (
    <Select
      value={value || '__none__'}
      onValueChange={(v) => onChange(v === '__none__' ? '' : v)}
    >
      <SelectTrigger className={cn('h-8 text-xs', className)} data-testid={testId}>
        <SelectValue placeholder="Assign" />
      </SelectTrigger>
      <SelectContent>
        {showNone && (
          <SelectItem value="__none__">
            <span className="text-muted-foreground">(unassigned)</span>
          </SelectItem>
        )}
        {people.map(p => (
          <SelectItem key={p.id} value={p.id}>
            <span className="inline-flex items-center gap-1.5">
              <User className="w-3 h-3 text-muted-foreground" />
              {p.name}{p.email ? <span className="text-muted-foreground ml-1">· {p.email}</span> : null}
            </span>
          </SelectItem>
        ))}
        {people.length === 0 && (
          <SelectItem value="__no__" disabled>No team members found</SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}
